/* eslint-disable jsx-a11y/alt-text */

import { ButtonMaster } from "@/components/Atoms/Buttons";
import CustomLink from "@/components/Atoms/CustomLink";
import Image from "@/components/Atoms/Image";
import { ProductBottleImage } from "@/components/Atoms/ProductBottleImage";
import { MultiPurposeParagraph } from "@/components/Atoms/Paragraph";
import { MultiPurposeTitle } from "@/components/Atoms/Title";
import {
	getProductDefinitionDisplayName,
	getProductImageAlt,
	getProductImageSrc,
	isOverwriteDefaultDataEnabled,
	resolveProductImage,
	resolveButtonCustomIcon,
	resolveButtonIconColor,
	slideUp,
} from "@/lib/functions";
import { routerHandler } from "@/lib/routes/router-links-handler";
import { resolvePortfolioBackgroundHex } from "@/lib/resolve-background-color";
import { LazyMotion, domAnimation, m } from "framer-motion";
import { Shape } from "@/components/Atoms/Spacer";
import { CustomUnderline, Underline } from "@/components/Atoms/Underline";
import styles from "./index.module.scss";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import GeoWrapper, {
	isAvailableForCountry,
	resolveCurrentCountry,
	resolveGeoRecord,
} from "@/components/Templates/Main/GeoConditional";
import { FullBackgroundImage } from "../ImageCard";
import { Color, SpacerConfiguration } from "types/types";
import ShopifySimpleBuyButton from "@/components/Organisms/Shopify/Simplebutton";

function isHalfColumn(column: string) {
	return column === "half" || column === "halfReduced";
}

function isHalfReducedColumn(column: string) {
	return column === "halfReduced";
}

/** Série Ímpar portfolio zigzag (`layoutColumnWidth: invert`). */
function isInvertColumn(column: string) {
	return column === "invert";
}

function getZigZagImageColumnClass(column: string) {
	if (isHalfReducedColumn(column)) return "lg:w-1/2 flex justify-center";
	if (isHalfColumn(column)) return "lg:w-1/2";
	if (column === "default") return "lg:w-4/6";
	return "lg:w-2/5";
}

function getZigZagCopyColumnClass(column: string) {
	if (isHalfReducedColumn(column)) return "lg:w-1/2 flex justify-center";
	if (isHalfColumn(column)) return "lg:w-1/2";
	if (column === "default") return "lg:w-2/5";
	return "lg:w-4/6";
}

function getHalfReducedInnerClass(column: string) {
	return isHalfReducedColumn(column) ? "w-full max-w-[500px]" : "w-full";
}

function getBgCardShellClass(column: string, isLeft: boolean) {
	const layout = isLeft
		? "flex-col lg:flex-row-reverse"
		: "flex-col lg:flex-row";

	if (isHalfReducedColumn(column)) {
		return `w-full max-w-[1000px] mx-auto px-6 lg:px-0 max-lg:overflow-visible rounded-md items-stretch flex ${layout} ${styles.bgCardHalfReducedShell}`;
	}

	if (isInvertColumn(column)) {
		return `w-full flex container m-auto items-stretch ${layout}`;
	}

	return `w-full flex container m-auto ${layout}`;
}

function getBgImageColumnClass(column: string) {
	if (isHalfReducedColumn(column)) {
		return "w-full flex lg:w-1/2 lg:min-h-0 max-lg:relative max-lg:z-20";
	}

	if (isInvertColumn(column)) {
		return `${getZigZagImageColumnClass(column)} relative w-full lg:self-stretch`;
	}

	return `${getZigZagImageColumnClass(column)} w-full`;
}

function getBgCopyColumnClass(column: string) {
	if (isHalfReducedColumn(column)) {
		return "lg:w-1/2 w-full flex max-lg:relative max-lg:z-10";
	}

	if (isInvertColumn(column)) {
		return `${getZigZagCopyColumnClass(column)} flex w-full lg:self-stretch`;
	}

	return `${getZigZagCopyColumnClass(column)} w-full`;
}

function getBgImageInnerClass(column: string) {
	if (isHalfReducedColumn(column)) {
		return `relative w-full lg:h-full flex flex-col ${styles.bgImageHalfReduced} ${styles.bgImageHalfReducedMobileHeroShell}`;
	}

	if (isInvertColumn(column)) {
		return `relative flex h-full min-h-0 w-full flex-col ${getHalfReducedInnerClass(column)}`;
	}

	return `relative flex flex-col gap-4 ${getHalfReducedInnerClass(column)}`;
}

function getBgCopyMainGridClass(column: string) {
	if (isHalfReducedColumn(column)) {
		return `${styles.bgCardHalfReducedCopyStack} w-full lg:flex lg:flex-col lg:items-start lg:gap-6`;
	}

	return "flex flex-col gap-4 items-start";
}

function getBgCopyMainColumnClass(column: string) {
	if (isHalfReducedColumn(column)) {
		return "flex min-w-0 flex-col items-start gap-4 max-lg:gap-3";
	}

	return "flex flex-col gap-4 items-start min-w-0";
}

function getBgCopyHalfReducedAlignClass(column: string, isLeft: boolean) {
	if (!isHalfReducedColumn(column)) return "";

	return isLeft
		? styles.bgCardHalfReducedCopyAlignLeft
		: styles.bgCardHalfReducedCopyAlignRight;
}

function getBgCopyInnerClass(column: string, isLeft = false) {
	if (isHalfReducedColumn(column)) {
		return `w-full h-full lg:flex flex-col justify-center relative ${styles.bgCardHalfReduced} ${getBgCopyHalfReducedAlignClass(column, isLeft)}`;
	}

	return `${getHalfReducedInnerClass(
		column
	)} grid grid-cols-1 w-full ${
		isInvertColumn(column) ? "h-full" : ""
	} lg:flex flex-col justify-center relative ${styles.bgCard} py-12`;
}

function getBgCopyContentClass(column: string, isLeft: boolean) {
	if (isHalfReducedColumn(column)) {
		return "flex flex-col items-start gap-6 xl:gap-12";
	}

	return `${
		isLeft ? "pr-0 lg:pr-20" : "pl-0 lg:pl-20"
	} flex flex-col items-start gap-6 xl:gap-12`;
}

function getBgProductOverlayShellClass(column: string) {
	if (isHalfReducedColumn(column)) {
		return `${styles.bgCardProductDesktopOverlay} absolute top-0 z-10 flex`;
	}

	return "flex absolute top-0 z-10";
}

function getBgProductOverlayPositionClass(
	column: string,
	isLeft: boolean,
	reduce: boolean
) {
	if (isHalfReducedColumn(column)) {
		return isLeft
			? "left-0 lg:translate-x-[-50%] lg:translate-y-[-13%]"
			: "right-0 lg:translate-x-[50%] lg:translate-y-[-13%]";
	}

	return isLeft
		? `translate-x-[-10%] lg:translate-x-[-50%] left-0 ${
				reduce
					? "top-[50%] -translate-y-[50%]"
					: " translate-y-[-13%]"
		  }`
		: `translate-x-[10%] lg:translate-x-[50%] right-0 ${
				reduce
					? "top-[50%] -translate-y-[50%]"
					: "translate-y-[-13%]"
		  }`;
}

function getProductOverlayMaxWidthClass(
	column: string,
	reduce: boolean,
	variant: "bg" | "uneven" = "bg"
) {
	if (isHalfReducedColumn(column)) return "";
	if (variant === "uneven") {
		return reduce
			? "max-w-[50%] "
			: "max-w-[55%] lg:max-w-[60%] xl:max-w-[20rem] ";
	}
	return reduce ? "max-w-[50%] " : "max-w-[65%] ";
}

export function ProductCard(props: any) {
	return (
		<>
			<div className="col-span-1">
				{props.data.media.map((media: any) => (
					<div
						key={media.id}
						className="flex items-center mx-auto justify-center "
					>
						<Image className={`w-80`} data={media.image} />
					</div>
				))}
			</div>
			{props.data.description && (
				<MultiPurposeParagraph
					data={props.data.description}
					color={undefined}
				/>
			)}
		</>
	);
}

interface ProductList {
	portfolioSlug: string;
	locale: any;
	data: any;
	text: any;
	style: any;
	btn: any;
	btnStyle: any;
	type: string;
}

export function ProductDefinitionList({
	portfolioSlug,
	locale,
	text,
	data,
	style,
	btnStyle,
	type,
	btn,
}: ProductList) {
	return (
		<div className=" xs:col-span-2 col-span-1 flex-none lg:flex-1 text-center flex flex-col  gap-2 relative z-[1] translate-y-[-15%] group">
			<div className="relative z-1">
				<CustomLink
					href={`${routerHandler({
						typename: data.product?.__typename,
						slug: [
							portfolioSlug,
							data.product.productTypeSpecific.slug,
							data.product?.slug,
						],
						locale: locale,
					})}`}
				>
					<ProductBottleImage
						product={data.product}
						className="max-w-[12rem] mx-auto group-hover:translate-y-[-5%] transition-all ease-in-out duration-300"
					/>
					<div className=" mx-auto px-4  flex flex-col justify-center items-center">
						<MultiPurposeParagraph
							data={data.product.productName}
							color={text?.color}
						/>
					</div>
				</CustomLink>
			</div>
			{type === "default" ? (
				<ButtonMaster
					anim={"slideTop"}
					type={"round"}
					cta={btnStyle?.color}
					color={btn?.color}
					link={`${routerHandler({
						typename: data.product?.__typename,
						slug: [
							portfolioSlug,
							data.product.productTypeSpecific.slug,
							data.product?.slug,
						],
						locale: locale,
					})}`}
				>
					ver mais
				</ButtonMaster>
			) : (
				<span
					style={{
						backgroundColor: style?.color.hex,
					}}
					className="absolute z-[-1] h-[65%] top-0 w-[12rem] left-0 right-0  m-auto  translate-y-[75%] rounded-t-[25%] rounded-b-[45%]"
				>
					<span
						style={{
							backgroundColor: style?.color.hex,
						}}
						className="absolute z-[-1] h-[12rem] top-0 w-[12rem] left-0 right-0  m-auto   translate-y-[-20%]  rounded-b-[45%]  rounded-t-[45%]"
					></span>
					<div className="absolute -bottom-4 left-0 right-0 m-auto w-fit group-hover:scale-110 transition-all ease-in-out duration-300">
						<ButtonMaster
							anim={"slideTop"}
							type={"round"}
							cta={btnStyle?.color}
							color={btn?.color}
							link={`${routerHandler({
								typename: data.product?.__typename,
								slug: [
									portfolioSlug,
									data.product.productTypeSpecific.slug,
									data.product?.slug,
								],
								locale: locale,
							})}`}
						>
							+
						</ButtonMaster>
					</div>
				</span>
			)}
		</div>
	);
}

interface ZigZag {
	awards: any;
	productsDefinition: any;
	portfolioSlug: any;
	locale: any;
	parallax: any;
	text: any;
	order: boolean;
	bgImage: any;
	column: any;
	reduce: boolean;
	type?: string;
	showTypeGeneric: boolean;
	underline: any;
	featuredSize: string;
	typeOfNavigation: boolean;
	productImageOrder: boolean;
	description: boolean;
	buttonConfig: any;
	buy?: any;
	/** Section background colour — used to detect same-colour buy button */
	bgColor?: any;
	bgColorGr?: any;
}

export function ZigZagList({
	productsDefinition,
	portfolioSlug,
	locale,
	parallax,
	text,
	order,
	bgImage,
	column,
	reduce,
	type,
	showTypeGeneric,
	productImageOrder,
	underline,
	featuredSize,
	typeOfNavigation,
	description,
	buttonConfig,
	buy,
	bgColor,
	bgColorGr,
}: ZigZag) {
	const { t } = useTranslation();
	const router = useRouter();
	const portfolioBackgroundHex = resolvePortfolioBackgroundHex(
		bgColorGr,
		bgColor
	);

	// Helper function to check if entry should be rendered based on geo
	const shouldRenderEntry = (definition: any) => {
		if (!router.isReady) return true;
		return isAvailableForCountry(
			resolveGeoRecord(definition?.geo),
			resolveCurrentCountry(router.query.country),
			router.query.ignoregeo
		);
	};

	// Filter entries based on geo conditions after router is ready (avoids SSR/client mismatch)
	const filteredProducts = productsDefinition?.filter((definition: any) => {
		if (!router.isReady) return true;
		return shouldRenderEntry(definition);
	});

	return (
		<>
			{filteredProducts?.map((definition: any, index: number) => {
				const style = {
					"--bg": definition?.color?.color?.hex,
					"--highlight": definition?.highlightColor?.color.hex,
					"--copy-text-color": text?.color?.hex,
				} as React.CSSProperties;
				const featureImage = definition.media?.filter(
					(image: any) => image.typeofimage === "image"
				)?.[0]?.image;

				const bgElement = definition.media?.filter(
					(image: any) => image.typeofimage === "bg"
				)?.[0]?.image;

				const isLeft = order ? index % 2 == 0 : index % 2 !== 0;

				const overwriteDescription = definition.customData.filter(
					(x: any) => x.typeofcontent == "pdescription"
				);
				const productBottleImage = resolveProductImage(definition?.product);

				if (type == "bg") {
					return (
						<div
							key={index}
							className={`flex ${
								isHalfReducedColumn(column) ? "w-full justify-center" : ""
							}`}
						>
							<div
								key={definition.id}
								className={`${getBgCardShellClass(column, isLeft)}
              ${!typeOfNavigation ? "scroll-mt-32" : ""}`}
								id={`${!typeOfNavigation ? definition.product?.slug : ""}`}
								style={
									isHalfReducedColumn(column)
										? ({
												"--bg": definition?.color?.color?.hex,
											} as React.CSSProperties)
										: undefined
								}
							>
								<div className={getBgImageColumnClass(column)}>
									<div className={getBgImageInnerClass(column)}>
									<div
										className={`relative w-full lg:h-full ${
											isHalfReducedColumn(column)
												? styles.bgImageHalfReducedMobile
												: ""
										}`}
										style={
											isHalfReducedColumn(column)
												? ({
														"--copy-text-color": text?.color?.hex,
														"--bg": definition?.color?.color?.hex,
													} as React.CSSProperties)
												: undefined
										}
									>
										<>
										<div
											className={
												isHalfReducedColumn(column)
													? styles.bgImageHalfReducedMobileFrame
													: isInvertColumn(column)
														? `relative h-full w-full ${styles.invertImageFrame}`
														: "relative h-full w-full"
											}
										>
											<>
											<Image
												data={featureImage}
												className={`w-full ${
													isHalfReducedColumn(column)
														? styles.bgImageHalfReducedFeature
														: isInvertColumn(column)
															? `h-full max-h-full ${styles.defaultImg} ${styles.invertImageMedia}`
															: `h-full ${
																	featuredSize == "ratio"
																		? styles.ratio
																		: ""
																} max-h-full`
												}`}
												objectFit={
													isHalfReducedColumn(column) || isInvertColumn(column)
														? "cover"
														: undefined
												}
											/>
											{isHalfReducedColumn(column) && (
												<div
													className={styles.bgImageHalfReducedGradient}
													aria-hidden="true"
												/>
											)}
											{productBottleImage && isHalfReducedColumn(column) && (
												<div
													className={`${styles.bgCardHalfReducedBottleOverlay} ${
														isLeft
															? styles.bgCardHalfReducedBottleOverlayLeft
															: styles.bgCardHalfReducedBottleOverlayRight
													}`}
												>
													<img
														src={getProductImageSrc(productBottleImage)}
														alt={getProductImageAlt(productBottleImage)}
														className={styles.bgCardProductBottleImage}
														loading="lazy"
													/>
												</div>
											)}
											{productBottleImage && !isHalfReducedColumn(column) && (
												<div
													className={`${getBgProductOverlayShellClass(column)} ${getProductOverlayMaxWidthClass(
														column,
														reduce,
														"bg"
													)} ${getBgProductOverlayPositionClass(column, isLeft, reduce)}`}
												>
													<ProductBottleImage data={productBottleImage} />
													{parallax[0] && (
														<LazyMotion features={domAnimation}>
															<m.div
																variants={slideUp()}
																initial="hidden"
																whileInView="visible"
																viewport={{ once: true }}
																exit="exit"
																className={`hidden lg:flex  absolute  max-w-[65%] right-0 left-0 top-0 bottom-0 h-fit m-auto z-[-1]
                           `}
															>
																{parallax[1] == "behind" && (
																	<Image data={parallax[0]} className="max-h-fit" />
																)}
															</m.div>
														</LazyMotion>
													)}
												</div>
											)}
											</>
										</div>
										{isHalfReducedColumn(column) && productBottleImage && (
											<div className={styles.bgCardProductMobileHero}>
												<img
													src={getProductImageSrc(productBottleImage)}
													alt={getProductImageAlt(productBottleImage)}
													className={styles.bgCardProductMobileHeroImage}
													loading="lazy"
												/>
											</div>
										)}
										</>
									</div>
									{parallax[0] && (
										<LazyMotion features={domAnimation}>
											{parallax[1] !== "behind" && (
												<>
													{" "}
													<m.div
														variants={slideUp()}
														initial="hidden"
														whileInView="visible"
														viewport={{ once: true }}
														exit="exit"
														className={`   ${
															isLeft ? "justify-end" : "justify-start"
														} flex`}
													>
														<Image data={parallax[0]} className="max-h-fit" />
													</m.div>
												</>
											)}
										</LazyMotion>
									)}
									</div>
								</div>
								<div className={getBgCopyColumnClass(column)}>
								<div
									style={style as React.CSSProperties}
									className={getBgCopyInnerClass(column, isLeft)}
								>
									{bgImage[0] && (
										<>
											{bgImage[2] == "below" && (
												<div
													className={`${
														isLeft
															? "lg:translate-x-[10rem]"
															: "lg:translate-x-[-5rem]"
													} absolute top-4 bottom-0 m-auto z-[0] opacity-70`}
												>
													<Image data={bgImage[1]} />
												</div>
											)}
										</>
									)}
									<div
										className={`flex flex-col relative z-10 ${
											isHalfReducedColumn(column)
												? "py-0"
												: "gap-24 py-12 lg:py-0"
										} ${
											isLeft
												? "order-2 justify-start lg:items-center"
												: "lg:justify-end lg:items-center"
										}`}
									>
										<div className={getBgCopyContentClass(column, isLeft)}>
											<div className={getBgCopyMainGridClass(column)}>
												<div className={getBgCopyMainColumnClass(column)}>
													<div className="flex flex-col gap-4">
														<MultiPurposeTitle
															tag="h3"
															type="default"
															data={getProductDefinitionDisplayName(definition)}
															color={text?.color}
														/>
														{underline?.length > 0 && (
															<>
																{underline[1]?.img ? (
																	<CustomUnderline
																		shape={underline[1]?.img}
																		color={underline[2]?.color}
																	/>
																) : (
																	<Underline color={underline[2]?.color} />
																)}
															</>
														)}
													</div>
													{showTypeGeneric && (
														<MultiPurposeParagraph
															type="default"
															data={
																definition.product.productOrigin
																	?.originDesignation ||
																definition?.product?.nonGeographicalClassification
																	?.nonGeographicalProductClassification
															}
															color={text?.color}
														/>
													)}
													{description && (
														<>
															{isOverwriteDefaultDataEnabled(
																definition.overwriteDefaultData
															) && overwriteDescription[0]?.content ? (
																<MultiPurposeParagraph
																	type="zigzagCopy"
																	data={overwriteDescription[0]?.content?.text}
																	color={text?.color}
																/>
															) : (
																<MultiPurposeParagraph
																	type="default"
																	data={
																		definition.product?.productDescriptionShort
																	}
																	color={text?.color}
																/>
															)}
														</>
													)}
													{definition?.showProductAwards &&
														definition?.product?.awardsAndRatings?.some(
															(r: any) => r?.rating?.ratingImage
														) && (
															<div>
																<MultiPurposeParagraph
																	type="default"
																	data={t("Ratings")}
																	color={underline[2]?.color}
																/>
																<div
																	className={` ${styles.awardList} flex flex-row gap-2`}
																>
																	{definition?.product?.awardsAndRatings?.map(
																		(rating: any, index: number) => (
																			<div key={index}>
																				<Image
																					data={rating?.rating?.ratingImage}
																					className="max-w-[4rem] max-h-16"
																				/>
																			</div>
																		)
																	)}
																</div>
															</div>
														)}
													<div>
														<ButtonMaster
															anim={"slideLeft"}
															type={buttonConfig?.buttonStyle}
															color={buttonConfig?.labelColor?.color}
															cta={buttonConfig?.typeColor?.color}
															customIcon={resolveButtonCustomIcon(buttonConfig)}
															customColor={resolveButtonIconColor(buttonConfig)}
															link={`${routerHandler({
																typename: definition.product?.__typename,
																slug: [
																	portfolioSlug,
																	definition.product.productTypeSpecific.slug,
																	definition.product?.slug,
																],
																locale: locale,
															})}`}
														>
															{" "}
															{buttonConfig?.buttonLabel?.label}
														</ButtonMaster>
													</div>
												</div>
											</div>
										</div>
									</div>
									<div
										className={` ${styles.highlightImage} hidden col-span-1 `}
									>
										<ProductBottleImage
											product={definition?.product}
											className="-top-[5rem] sm:-top-28 z-10"
										/>
									</div>
								</div>
								</div>
							</div>
						</div>
					);
				} else if (type == "uneven") {
					return (
						<div key={index} className="flex">
							<div
								key={definition.id}
								className={`w-full flex container m-auto items-stretch ${
									isLeft
										? "flex-col lg:flex-row-reverse"
										: "flex-col lg:flex-row"
								}
          ${!typeOfNavigation ? "scroll-mt-32" : ""}`}
								id={`${!typeOfNavigation ? definition.product?.slug : ""}`}
							>
								<div
									className={`${getZigZagImageColumnClass(
										column
									)} relative w-full lg:self-stretch`}
								>
									<div
										className={`relative flex h-full min-h-0 w-full flex-col ${getHalfReducedInnerClass(
											column
										)}`}
									>
									<div
										className={`relative w-full ${styles.unevenImageFrame}`}
									>
										<Image
											data={featureImage}
											className={`h-full w-full ${
												featuredSize == "ratio" ? `${styles.ratio}` : ""
											} ${styles.defaultImg} ${styles.unevenImageMedia}`}
											objectFit="cover"
										/>
										<div
											className={`absolute top-0 z-10 flex ${getProductOverlayMaxWidthClass(
												column,
												reduce,
												"uneven"
											)}${
												isLeft
													? `translate-x-[-10%]  lg:translate-x-[-50%]  left-0 ${
															reduce
																? "top-[50%] -translate-y-[50%]"
																: " translate-y-[-15%] sm:translate-y-[-25%] lg:translate-y-[-15%]"
													  }`
													: `translate-x-[10%] lg:translate-x-[50%]  right-0 ${
															reduce
																? "top-[50%] -translate-y-[50%]"
																: "translate-y-[-15%] sm:translate-y-[-25%] lg:translate-y-[-15%]"
													  }`
											}`}
										>
											<ProductBottleImage
												data={productBottleImage}
											/>
											{parallax[0] && (
												<LazyMotion features={domAnimation}>
													<m.div
														variants={slideUp()}
														initial="hidden"
														whileInView="visible"
														viewport={{ once: true }}
														exit="exit"
														className={`hidden lg:flex  absolute  max-w-[65%] right-0 left-0 top-0 bottom-0 h-fit m-auto z-[-1]
                       `}
													>
														{parallax[1] == "behind" && (
															<Image data={parallax[0]} className="max-h-fit" />
														)}
													</m.div>
												</LazyMotion>
											)}
										</div>
									</div>
									{parallax[0] && (
										<LazyMotion features={domAnimation}>
											{parallax[1] !== "behind" && (
												<>
													{" "}
													<m.div
														variants={slideUp()}
														initial="hidden"
														whileInView="visible"
														viewport={{ once: true }}
														exit="exit"
														className={`   ${
															isLeft ? "justify-end" : "justify-start"
														} flex`}
													>
														<Image data={parallax[0]} className="max-h-fit" />
													</m.div>
												</>
											)}
										</LazyMotion>
									)}
									</div>
								</div>
								<div
									className={`${getZigZagCopyColumnClass(
										column
									)} flex w-full lg:self-stretch`}
								>
								<div
									style={style as React.CSSProperties}
									className={`${getHalfReducedInnerClass(
										column
									)} grid grid-cols-1 w-full h-full lg:flex flex-col justify-center relative lg:translate-y-[2rem] ${
										styles.bgCard
									} py-12`}
								>
									{bgImage[0] && (
										<>
											{bgImage[2] == "below" && (
												<div
													className={`${
														isLeft
															? "lg:translate-x-[10rem]"
															: "lg:translate-x-[-5rem]"
													} absolute top-4 bottom-0 m-auto z-[0] opacity-70`}
												>
													<Image data={bgImage[1]} />
												</div>
											)}
										</>
									)}
									<div
										className={` flex flex-col gap-24 py-12 lg:py-0 relative z-10 ${
											isLeft
												? "order-2 justify-start lg:items-center  "
												: " lg:justify-end lg:items-center "
										}`}
									>
										<div
											className={`${
												isLeft ? ` pr-0 lg:pr-20` : ` pl-0  lg:pl-20`
											}  flex flex-col items-start gap-6 xl:gap-12 `}
										>
											<div className={`flex flex-col gap-4 items-start`}>
												<div className="flex flex-col gap-4">
													<MultiPurposeTitle
														tag="p"
														type={"featured"}
														data={getProductDefinitionDisplayName(definition)}
														color={text?.color}
													/>
													{underline?.length > 0 && (
														<>
															{underline[1]?.img ? (
																<CustomUnderline
																	shape={underline[1]?.img}
																	color={underline[2]?.color}
																/>
															) : (
																<Underline color={underline[2]?.color} />
															)}
														</>
													)}
												</div>
												{showTypeGeneric && (
													<MultiPurposeParagraph
														type="default"
														data={
															definition.product.productOrigin
																?.originDesignation ||
															definition?.product?.nonGeographicalClassification
																?.nonGeographicalProductClassificatio
														}
														color={text?.color}
													/>
												)}
												{description && (
													<>
														{isOverwriteDefaultDataEnabled(
															definition.overwriteDefaultData
														) && overwriteDescription[0]?.content ? (
															<MultiPurposeParagraph
																type="zigzagCopy"
																data={overwriteDescription[0]?.content?.text}
																color={text?.color}
															/>
														) : (
															<>
																<MultiPurposeParagraph
																	type={"default"}
																	data={
																		definition.product?.productDescriptionShort
																	}
																	color={text?.color}
																/>
															</>
														)}
													</>
												)}
											</div>
											{definition?.showProductAwards &&
												definition?.product?.awardsAndRatings?.some(
													(r: any) => r?.rating?.ratingImage
												) && (
													<div>
														<MultiPurposeParagraph
															type="default"
															data={t("Ratings")}
															color={underline[2]?.color}
														/>
														<div
															className={` ${styles.awardList} flex flex-row gap-2`}
														>
															{definition?.product?.awardsAndRatings?.map(
																(rating: any, index: number) => {
																	return (
																		<div>
																			<Image
																				key={index}
																				data={rating?.rating?.ratingImage}
																				className="max-w-[4rem] max-h-16"
																			/>
																		</div>
																	);
																}
															)}
														</div>

														<div className="flex gap-4"></div>
													</div>
												)}
											<div>
												<ButtonMaster
													anim={"slideLeft"}
													type={buttonConfig?.buttonStyle}
													color={buttonConfig?.labelColor?.color}
													cta={buttonConfig?.typeColor?.color}
													customIcon={resolveButtonCustomIcon(buttonConfig)}
													customColor={resolveButtonIconColor(buttonConfig)}
													link={`${routerHandler({
														typename: definition.product?.__typename,
														slug: [
															portfolioSlug,
															definition.product.productTypeSpecific.slug,
															definition.product?.slug,
														],
														locale: locale,
													})}`}
												>
													{" "}
													{buttonConfig?.buttonLabel?.label}
												</ButtonMaster>
											</div>
										</div>
									</div>
									<div
										className={` ${styles.highlightImage} hidden col-span-1 `}
									>
										<ProductBottleImage
											product={definition?.product}
											className="-top-[5rem] sm:-top-28 z-10"
										/>
									</div>
								</div>
								</div>
							</div>
						</div>
					);
				} else {
					return (
						<div
							key={index}
							className="relative w-full"
							style={
								type == "high"
									? { backgroundColor: definition?.color?.color?.hex }
									: {}
							}
						>
							{bgImage[0] && bgImage[1] && bgImage[2] !== "below" && (
								<div
									className="absolute inset-0 z-0 pointer-events-none"
									style={{
										backgroundImage: `url(${bgImage[1]?.url})`,
										backgroundRepeat: "repeat",
										backgroundSize: "auto",
										backgroundPosition: "0 0",
										opacity: 0.3,
										width: "100%",
										height: "100%",
									}}
								/>
							)}
							{type == "high" && bgElement && (
								<div className="absolute inset-0 z-0 w-full pointer-events-none">
									<FullBackgroundImage position="cover" data={bgElement} />
								</div>
							)}
							<div
								className={`w-full flex ${
									type == "high" ? `px-12 lg:px-32 py-12 lg:py-24` : ``
								} container mx-auto max-w-[1360px] relative z-10 ${
									isLeft
										? "flex-col lg:flex-row-reverse"
										: "flex-col lg:flex-row"
								}
                ${!typeOfNavigation ? "scroll-mt-32" : ""}`}
								id={`${!typeOfNavigation ? definition.product?.slug : ""}`}
							>
								{type != "high" && bgElement && (
									<div className="absolute inset-0 z-0 pointer-events-none">
										<FullBackgroundImage position="cover" data={bgElement} />
									</div>
								)}
								<div className={`${getZigZagImageColumnClass(column)} w-full`}>
									<div
										className={`relative flex flex-col gap-4 ${getHalfReducedInnerClass(
											column
										)}`}
									>
									<CustomLink
										key={index}
										href={`${routerHandler({
											typename: definition.product?.__typename,
											slug: [
												portfolioSlug,
												definition.product.productTypeSpecific.slug,
												definition.product?.slug,
											],
											locale: locale,
										})}`}
										className="flex relative w-full"
									>
										<div className="relative h-full w-full">
											<Image
												data={featureImage}
												className={`h-full ${
													featuredSize == "ratio" ? `${styles.ratio}` : ""
												} max-h-fit w-full`}
											/>
											<SkuImageContainer
												align={isLeft}
												reduce={reduce}
												type={type}
												style={style}
												invertProduct={productImageOrder}
												noMaxWidth={isHalfReducedColumn(column)}
											>
												<ProductBottleImage
													product={definition?.product}
												/>
												{parallax[0] && (
													<LazyMotion features={domAnimation}>
														<m.div
															variants={slideUp()}
															initial="hidden"
															whileInView="visible"
															viewport={{ once: true }}
															exit="exit"
															className={`hidden lg:flex  absolute  max-w-[65%] right-0 left-0 top-0 bottom-0 h-fit m-auto z-[-1]
     `}
														>
															{parallax[1] == "behind" && (
																<Image
																	data={parallax[0]}
																	className="max-h-fit"
																/>
															)}
														</m.div>
													</LazyMotion>
												)}
											</SkuImageContainer>
										</div>
									</CustomLink>
									{parallax[0] && (
										<LazyMotion features={domAnimation}>
											{parallax[1] !== "behind" && (
												<>
													{" "}
													<m.div
														variants={slideUp()}
														initial="hidden"
														whileInView="visible"
														viewport={{ once: true }}
														exit="exit"
														className={`   ${
															isLeft ? "justify-end" : "justify-start"
														} flex`}
													>
														<Image data={parallax[0]} className="max-h-fit" />
													</m.div>
												</>
											)}
										</LazyMotion>
									)}
									</div>
								</div>
								<div className={`${getZigZagCopyColumnClass(column)} w-full`}>
								<div
									style={style as React.CSSProperties}
									className={`${getHalfReducedInnerClass(
										column
									)} grid grid-cols-2 w-full lg:flex flex-col justify-center relative ${
										type == "bg" ? `${styles.bgCard} py-12` : ""
									}`}
								>
									{bgImage[0] && (
										<>
											{bgImage[2] == "below" && (
												<div
													className={`${
														isLeft
															? "lg:translate-x-[30%]"
															: "lg:translate-x-[-30%]"
													} absolute top-4 bottom-0 m-auto z-[0] opacity-70`}
												>
													<Image data={bgImage[1]} />
												</div>
											)}
										</>
									)}
									<div
										className={` flex flex-col gap-24 py-12 lg:py-0 relative z-10 ${
											isLeft
												? "order-2 justify-start lg:items-center md:items-end "
												: " lg:justify-end lg:items-center "
										}`}
									>
										<div
											className={`${
												isLeft ? " lg:pr-[10rem]" : " lg:pl-[10rem]"
											}  flex flex-col items-start gap-6 ${
												type === "bg"
													? "xl:gap-12"
													: type == "high"
													? ""
													: "xl:gap-24"
											} `}
										>
											<div className={`flex flex-col gap-4 items-start`}>
												<div className="flex flex-col gap-4">
													{type == "high" ? (
														<MultiPurposeTitle
															tag="h3"
															type="featured"
															data={getProductDefinitionDisplayName(definition)}
															color={text?.color}
														/>
													) : (
														<MultiPurposeTitle
															tag="h3"
															type="default"
															data={getProductDefinitionDisplayName(definition)}
															color={text?.color}
														/>
													)}

													{underline[0] && (
														<>
															{underline[1]?.img ? (
																<CustomUnderline
																	shape={underline[1]?.img}
																	color={underline[2]?.color}
																/>
															) : (
																<Underline color={underline[2]?.color} />
															)}
														</>
													)}
												</div>
												{description && (
													<div className="hidden lg:block">
														{isOverwriteDefaultDataEnabled(
															definition.overwriteDefaultData
														) && overwriteDescription[0]?.content ? (
															<MultiPurposeParagraph
																type="zigzagCopy"
																data={overwriteDescription[0]?.content?.text}
																color={text?.color}
															/>
														) : (
															<>
																<MultiPurposeParagraph
																	type="default"
																	data={
																		definition.product?.productDescriptionShort
																	}
																	color={text?.color}
																/>
															</>
														)}
													</div>
												)}
												{showTypeGeneric && (
													<MultiPurposeParagraph
														type="default"
														data={
															definition.product.productOrigin
																?.originDesignation ||
															definition.product?.nonGeographicalClassification
																?.nonGeographicalProductClassificatio
														}
														color={text?.color}
													/>
												)}
											</div>
											{definition?.showProductAwards &&
												definition?.product?.awardsAndRatings?.some(
													(r: any) => r?.rating?.ratingImage
												) && (
												<div>
													<div className="flex gap-4"></div>
												</div>
											)}

											<div className="flex flex-col gap-4">
												<ButtonMaster
													anim={"slideLeft"}
													type={buttonConfig?.buttonStyle}
													color={buttonConfig?.labelColor?.color}
													cta={buttonConfig?.typeColor?.color}
													customIcon={resolveButtonCustomIcon(buttonConfig)}
													customColor={resolveButtonIconColor(buttonConfig)}
													link={`${routerHandler({
														typename: definition.product?.__typename,
														slug: [
															portfolioSlug,
															definition.product.productTypeSpecific.slug,
															definition.product?.slug,
														],
														locale: locale,
													})}`}
												>
													{" "}
													{buttonConfig?.buttonLabel?.label}
												</ButtonMaster>
												{definition.showBuyOnPortfolio && (
													<>
														{" "}
														{definition?.buyButton?.shopifyProductId && (
															<GeoWrapper
																list={
																	Array.isArray(
																		definition?.buyButton?.ctaGeolocation
																	)
																		? definition?.buyButton?.ctaGeolocation?.[0]
																				?.listOfCountries
																		: definition?.buyButton?.ctaGeolocation
																				?.listOfCountries
																}
																country={
																	Array.isArray(
																		definition?.buyButton?.ctaGeolocation
																	)
																		? definition?.buyButton?.ctaGeolocation?.[0]
																				?.countries
																		: definition?.buyButton?.ctaGeolocation
																				?.countries
																}
																initial={
																	Array.isArray(
																		definition?.buyButton?.ctaGeolocation
																	)
																		? definition?.buyButton?.ctaGeolocation?.[0]
																				?.countryAvailability
																		: definition?.buyButton?.ctaGeolocation
																				?.countryAvailability
																}
															>
																<ShopifySimpleBuyButton
																	domain={buy?.[1]}
																	storefrontAccessToken={buy?.[0]}
																	productId={
																		definition?.buyButton?.shopifyProductId
																	}
																	type={buy?.[4]?.buttonStyle}
																	customColor={buy?.[4]?.labelColor?.color}
																	label={definition?.buyButton?.label?.label}
																	color={buy?.[4]?.labelColor?.color}
																	cta={buy?.[4]?.typeColor?.color}
																	config={buy?.[3]}
																	cart={buy?.[3]}
																	disableQuantity={true}
																	surfaceBackgroundHex={
																		type === "bg"
																			? definition?.color?.color?.hex
																			: portfolioBackgroundHex
																	}
																	brand={buy?.[5]}
																/>
															</GeoWrapper>
														)}
													</>
												)}
											</div>
										</div>
									</div>
									<div
										className={`${styles.highlightImage} flex col-span-1 lg:hidden relative`}
									>
										<div style={style} className={styles.highlightMobile}></div>
										<ProductBottleImage
											product={definition?.product}
											className="-top-[5rem] sm:-top-28 z-10"
										/>
									</div>
								</div>
								</div>
							</div>
						</div>
					);
				}
			})}
		</>
	);
}

interface GridProps {
	productsDefinition: Array<{
		productLabelDc: any;
		brandLabelDc: any;
		label: string;
		customLabel: any;
		brandLabel: any;
		product: {
			productName: string;
			__typename: string;
			productBrand: {
				title: string;
			};
			productTypeSpecific: {
				slug: string;
			};
			slug: string;
			vintageYear: string;
			factSheetImage?: any;
			sku?: Array<{
				skuImageGallery?: Array<any>;
			}>;
		};
	}>;
	portfolioSlug: string;
	locale: string | undefined;
	text?: {
		color: Color["color"];
	};
	spacer: [boolean, SpacerConfiguration?];
	bgColor?: string;
	buttonConfig?: {
		buttonStyle?:
			| "default"
			| "borderBottom"
			| "round"
			| "borderFull"
			| "borderFullRound"
			| "bgFill"
			| "bgFillSquare"
			| "bgFillRadius"
			| "bgFillRound"
			| null
			| undefined;
		labelColor?: Color;
		typeColor?: Color;
		icon?: {
			img: any;
		};
		iconColor?: Color;
		buttonLabel?: {
			label: string;
		};
	};
	type?: "vertical" | "default" | "bg" | undefined;
	brand: boolean;
}

export function GridList(props: GridProps) {
	const { spacer, bgColor, brand, type = "default" } = props;

	return (
		<>
			{spacer[0] && spacer[1] && (
				<Shape
					shape={spacer[1].customShape?.img.url}
					color={bgColor}
					direction={spacer[1].spacerDirection}
					icon={[spacer[1].addIconElement, spacer[1].spacerIcon?.img.url]}
					position={
						spacer[1].spacerPosition === "invert"
							? undefined
							: spacer[1].spacerPosition
					}
					invert={false}
				/>
			)}
			<div className="container mx-auto py-24">
				{type === "vertical" ? (
					<VerticalCardGrid {...props} />
				) : (
					<DefaultCardGrid {...props} />
				)}
			</div>
		</>
	);
}
interface SkuImageContainer {
	children: any;
	reduce: boolean;
	align: boolean;
	invertProduct: boolean;
	type: string | undefined;
	style: any;
	noMaxWidth?: boolean;
}

function getSkuContainerMaxWidth(reduce: boolean, noMaxWidth?: boolean, variant?: "bg" | "high" | "default") {
	if (noMaxWidth) return "";
	if (variant === "bg") {
		return reduce
			? "max-w-[45%] top-[50%] -translate-y-[50%] "
			: "max-w-[65%]  translate-y-[-13%]";
	}
	if (variant === "high") {
		return reduce
			? "max-w-[45%] top-[50%] -translate-y-[30%] "
			: "max-w-[65%]  translate-y-[-13%]";
	}
	return reduce
		? "max-w-[55%] translate-y-[-5%] "
		: "max-w-[65%]  translate-y-[-13%]";
}

export function SkuImageContainer({ ...props }: SkuImageContainer) {
	switch (props.type) {
		case "bg":
			return (
				<>
					{" "}
					{props.align ? (
						<div
							className={`hidden lg:flex  absolute top-0 z-10 ${getSkuContainerMaxWidth(
								props.reduce,
								props.noMaxWidth,
								"bg"
							)} translate-x-[-50%]  left-0`}
						>
							{props.children}
						</div>
					) : (
						<div
							className={`hidden lg:flex  absolute top-0 z-10 ${getSkuContainerMaxWidth(
								props.reduce,
								props.noMaxWidth,
								"bg"
							)} translate-x-[50%]  right-0`}
						>
							{props.children}
						</div>
					)}
				</>
			);
			break;
		case "high":
			return (
				<>
					{" "}
					{props.align ? (
						<div
							className={`hidden lg:flex  absolute top-0 z-10 h-full   ${getSkuContainerMaxWidth(
								props.reduce,
								props.noMaxWidth,
								"high"
							)} ${
								props.invertProduct
									? "translate-x-[-50%]"
									: "translate-x-[50%] right-0"
							} top-0  m-auto `}
						>
							<div style={props.style} className={styles.highlight}></div>
							{props.children}
						</div>
					) : (
						<div
							className={`hidden lg:flex  absolute top-0 z-10 h-full ${getSkuContainerMaxWidth(
								props.reduce,
								props.noMaxWidth,
								"high"
							)} ${
								props.invertProduct
									? "translate-x-[50%] right-0"
									: "translate-x-[-50%]"
							} top-0  m-auto `}
						>
							{props.children}
							<div style={props.style} className={styles.highlight}></div>
						</div>
					)}
				</>
			);
			break;
		default:
			return (
				<>
					{" "}
					{props.align ? (
						<div
							className={`hidden lg:flex  absolute top-0 z-10 ${getSkuContainerMaxWidth(
								props.reduce,
								props.noMaxWidth,
								"default"
							)} translate-x-[-50%]  left-0 top-0  m-auto `}
						>
							{props.children}
						</div>
					) : (
						<div
							className={`hidden lg:flex  absolute top-0 z-10 ${getSkuContainerMaxWidth(
								props.reduce,
								props.noMaxWidth,
								"default"
							)} translate-x-[50%]  right-0 top-0  m-auto `}
						>
							{props.children}
						</div>
					)}
				</>
			);
			break;
	}
}

const DefaultCardGrid: React.FC<GridProps> = ({
	productsDefinition,
	portfolioSlug,
	locale,
	text,
	buttonConfig,
}) => {
	return (
		<div className="max-w-[1360px] grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 m-auto gap-24">
			{productsDefinition?.map((definition, index) => (
				<CustomLink
					key={index}
					className="group"
					href={`${routerHandler({
						typename: definition.product.__typename,
						slug: [
							portfolioSlug,
							definition.product.productTypeSpecific.slug,
							definition.product.slug,
						],
						locale: locale,
					})}`}
				>
					<div className="col-span-1">
						<div className="flex flex-row justify-center">
							<div className="flex flex-col lg:items-center justify-end">
								<div className="flex flex-col gap-2 items-start pb-6 relative">
									<MultiPurposeTitle
										tag="p"
										type="featured"
										data={definition.product.productBrand.title}
										color={text?.color}
									/>
									<MultiPurposeParagraph
										color={text?.color}
										type={"featured"}
										data={`${definition.product.vintageYear}`}
									/>

									<div className="absolute bottom-0 min-w-[10rem] translate-y-[100%]">
										<ButtonMaster
											anim="slideLeft"
											type={buttonConfig?.buttonStyle}
											color={buttonConfig?.labelColor?.color}
											cta={buttonConfig?.typeColor?.color}
											customIcon={resolveButtonCustomIcon(buttonConfig)}
											customColor={resolveButtonIconColor(buttonConfig)}
											link={`${routerHandler({
												typename: definition.product.__typename,
												slug: [
													portfolioSlug,
													definition.product.productTypeSpecific.slug,
													definition.product.slug,
												],
												locale: locale,
											})}`}
										>
											{buttonConfig?.buttonLabel?.label}
										</ButtonMaster>
									</div>
								</div>
							</div>
							<div className="flex group-hover:scale-110 duration-300 transition-all">
								<ProductBottleImage
									product={definition.product}
									className="max-w-[15rem]"
								/>
							</div>
						</div>
					</div>
				</CustomLink>
			))}
		</div>
	);
};

// Vertical Card Grid Component
const VerticalCardGrid: React.FC<GridProps> = ({
	productsDefinition,
	portfolioSlug,
	locale,
	text,
	buttonConfig,
	brand,
}) => {
	return (
		<div className="max-w-[1360px] grid grid-cols-2 lg:grid-cols-4 gap-8 m-auto">
			{productsDefinition?.map((definition, index) => (
				<CustomLink
					key={index}
					className="group"
					href={`${routerHandler({
						typename: definition.product.__typename,
						slug: [
							portfolioSlug,
							definition.product.productTypeSpecific.slug,
							definition.product.slug,
						],
						locale: locale,
					})}`}
				>
					<div className="w-full flex flex-col items-center gap-8 p-6 hover:bg-gray-50 min-h-[29rem] md:min-h-[35rem] justify-between transition-all duration-300">
						<div className="flex flex-col gap-4">
							<ProductBottleImage
								product={definition.product}
								className={` w-full group-hover:scale-105 transition-transform duration-300  ${styles.vertical} `}
							/>
							{brand && (
								<MultiPurposeParagraph
									transform={"uppercase"}
									type="label"
									data={
										definition?.brandLabelDc?.label ||
										definition.product.productBrand.title
									}
									align={"center"}
									color={text?.color!}
								/>
							)}

							<MultiPurposeParagraph
								data={getProductDefinitionDisplayName(definition, {
									requireCustomLabelFlag: true,
								})}
								color={text?.color}
								type={"subLabel"}
								align={"center"}
								font={"sans"}
								weight={"bold"}
							/>
						</div>

						<div className="flex flex-col gap-4">
							<ButtonMaster
								anim="slideLeft"
								type={buttonConfig?.buttonStyle}
								color={buttonConfig?.labelColor?.color}
								cta={buttonConfig?.typeColor?.color}
								customIcon={resolveButtonCustomIcon(buttonConfig)}
								customColor={resolveButtonIconColor(buttonConfig)}
								link={`${routerHandler({
									typename: definition.product.__typename,
									slug: [
										portfolioSlug,
										definition.product.productTypeSpecific.slug,
										definition.product.slug,
									],
									locale: locale,
								})}`}
							>
								{buttonConfig?.buttonLabel?.label}
							</ButtonMaster>
						</div>
					</div>
				</CustomLink>
			))}
		</div>
	);
};
